import React from 'react';
import { Check } from 'lucide-react';
import BottomSheet from '../../../ui/BottomSheet';
import List from '../../../ui/List';
import Badge from '../../../ui/Badge';
import SectionHeader from '../../../ui/SectionHeader';
import { useActionConfirm } from '../hooks/useActionConfirm';
import './ActionSheet.css';

/**
 * Aktionsliste als `BottomSheet` am Handy: „Mehr" der Aktionsleiste und der Langdruck auf die
 * freie Brettfläche (Spec 2026-10-21 §5.5).
 *
 *   <ActionSheet open={open} onOpenChange={setOpen} title="Mehr" items={sessionActions(…)} />
 *
 * - `items`: Einträge aus `utils/cardActions.js`. Ein Tipp schließt das Sheet und führt den
 *   Eintrag aus; Einträge mit `confirm` fragen danach per `ConfirmDialog` nach (der Dialog
 *   lebt außerhalb des Sheets, das Schließen nimmt ihn nicht mit).
 * - `title` Vorgabe „Aktionen". Zurück-Geste und Wischen schließen über `BottomSheet`.
 */
export default function ActionSheet({ open, onOpenChange, title = 'Aktionen', items = [] }) {
  const { select, dialog } = useActionConfirm();
  return (
    <>
      <BottomSheet open={open} onOpenChange={onOpenChange} title={title}>
        <ActionList
          actions={items}
          onSelect={(entry) => {
            onOpenChange?.(false);
            select(entry);
          }}
        />
      </BottomSheet>
      {dialog}
    </>
  );
}

/**
 * Einträge als `List`; ein Eintrag mit `children` wird zum Abschnitt mit Überschrift, in dem
 * seine Kinder stehen. `badge` steht als Zahl, `checked` als Häkchen rechts.
 * `onSelect(entry)` entscheidet, was ein Tipp auslöst. Geteilt mit `CardActionSheet`.
 */
export function ActionList({ actions, onSelect }) {
  const blocks = [];
  actions.forEach((entry) => {
    const last = blocks[blocks.length - 1];
    if (entry.children) blocks.push({ section: entry });
    else if (last?.items) last.items.push(entry);
    else blocks.push({ items: [entry] });
  });

  return (
    <div className="action-list">
      {blocks.map((block) => (block.section ? (
        <section key={block.section.id} className="action-list__section" aria-label={block.section.label}>
          <SectionHeader size="sm">{block.section.label}</SectionHeader>
          <ActionList actions={block.section.children} onSelect={onSelect} />
        </section>
      ) : (
        <List key={block.items[0].id}>
          {block.items.map((entry) => <ActionRow key={entry.id} entry={entry} onSelect={onSelect} />)}
        </List>
      )))}
    </div>
  );
}

function ActionRow({ entry, onSelect }) {
  const Icon = entry.icon;
  const hasExtra = entry.badge != null || entry.checked;
  return (
    <List.Item onClick={() => onSelect(entry)} disabled={entry.disabled}>
      {Icon && (
        <span className={`action-list__icon${entry.danger ? ' action-list__icon--danger' : ''}`} aria-hidden>
          <Icon size={18} />
        </span>
      )}
      <List.Main>
        <List.Name>
          <span className={entry.danger ? 'action-list__label--danger' : undefined}>{entry.label}</span>
        </List.Name>
      </List.Main>
      {hasExtra && (
        <List.Actions>
          {entry.badge != null && <Badge>{entry.badge}</Badge>}
          {entry.checked && <Check size={18} className="action-list__check" aria-label="gesetzt" />}
        </List.Actions>
      )}
    </List.Item>
  );
}
